import { Service, computed, inject, signal } from '@angular/core';
import { form, maxLength, pattern, required, requiredError, validate } from '@angular/forms/signals';
import {
  AgendamentoEscolhido,
  CampoFormularioPublicoResponse,
  ErroSolicitacaoServico,
  ModoAcessoSolicitacao,
  SolicitacaoAberturaResponse,
  UFS,
} from '../models/solicitacao-servico.model';
import { DesafioDoFormulario, DesafioFormularioService } from '../services/desafio-formulario.service';
import {
  DadosAberturaSolicitacao,
  SolicitacaoServicoPublicaService,
  montarFormDataSolicitacao,
} from '../services/solicitacao-servico-publica.service';

/** Os campos fixos da etapa Solicitação e as observações da Confirmação. */
export interface DadosSolicitacaoServico {
  endereco: string;
  numero: string;
  complemento: string;
  cep: string;
  bairro: string;
  cidade: string;
  uf: string;
  descricao: string;
  observacoes: string;
}

/** Um anexo escolhido. Guarda o `File` inteiro: é ele que viaja no multipart. */
export interface ArquivoAnexoSolicitacao {
  id: number;
  nome: string;
  tamanho: number;
  arquivo: File;
}

export const LIMITE_DESCRICAO = 500;
export const LIMITE_OBSERVACOES = 500;
export const LIMITE_TEXTO_CURTO = 300;
export const LIMITE_TEXTO_LONGO = 2000;

const MENSAGEM_OBRIGATORIO = 'Campo obrigatório.';

const VALOR_INICIAL: DadosSolicitacaoServico = {
  endereco: '',
  numero: '',
  complemento: '',
  cep: '',
  bairro: '',
  cidade: '',
  uf: '',
  descricao: '',
  observacoes: '',
};

/** Formato do catálogo → extensões aceitas. O mesmo mapa do back-end. */
const EXTENSOES_POR_FORMATO: Readonly<Record<string, readonly string[]>> = {
  PDF: ['.pdf'],
  JPG: ['.jpg', '.jpeg'],
  PNG: ['.png'],
  DOC: ['.doc'],
  DOCX: ['.docx'],
};

const TODOS_OS_FORMATOS: readonly string[] = Object.keys(EXTENSOES_POR_FORMATO);

const DEZ_MEGABYTES = 10 * 1024 * 1024;

/** O valor do atributo `accept` do seletor de arquivos para os formatos dados. */
export function acceptDosFormatos(formatos: readonly string[]): string {
  return formatos.flatMap((formato) => EXTENSOES_POR_FORMATO[formato] ?? []).join(',');
}

export function formatarTamanho(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Confere UM arquivo contra formatos e teto — a mesma regra que o back-end aplica antes do
 * upload (ele ainda confere o tipo real pelos bytes, que o navegador não tem como fazer).
 * Devolve a mensagem de recusa, ou `null` quando o arquivo é aceito.
 */
export function conferirArquivo(
  arquivo: File,
  formatos: readonly string[],
  tetoBytes: number,
): string | null {
  const extensao = arquivo.name.includes('.') ? arquivo.name.slice(arquivo.name.lastIndexOf('.')).toLowerCase() : '';
  const aceito = formatos.some((formato) => (EXTENSOES_POR_FORMATO[formato] ?? []).includes(extensao));

  if (!aceito) {
    return `"${arquivo.name}" não está num formato aceito (${formatos.join(', ')}).`;
  }

  if (arquivo.size <= 0) return `"${arquivo.name}" está vazio.`;

  if (arquivo.size > tetoBytes) {
    return `"${arquivo.name}" passa do tamanho máximo de ${formatarTamanho(tetoBytes)}.`;
  }

  return null;
}

/**
 * O estado do fluxo de solicitação de serviço.
 *
 * Provido pelo `PagesServicosComponent` (`providers`), e não na raiz: cada etapa é destruída
 * pelo `@switch` a cada troca de passo, e os dados precisam sobreviver às idas e vindas —
 * inclusive os `File` dos anexos. É o desenho do `ManifestacaoStore` da Ouvidoria.
 *
 * O formulário do serviço (campos, regras de anexo, agendamento) vem do
 * `SolicitacaoServicoPublicaService`, na raiz; o store só o lê.
 */
@Service({ autoProvided: false })
export class SolicitacaoServicoStore {
  private readonly api = inject(SolicitacaoServicoPublicaService);

  readonly modoAcesso = signal<ModoAcessoSolicitacao>('');

  readonly anonimo = computed(() => this.modoAcesso() === 'anonimo');

  readonly formulario = this.api.formulario;
  readonly carregandoFormulario = this.api.carregandoFormulario;
  readonly falhouFormulario = this.api.falhouFormulario;

  readonly campos = computed<readonly CampoFormularioPublicoResponse[]>(() => this.formulario()?.campos ?? []);

  readonly exigeAgendamento = computed(() => this.formulario()?.bitExigeAgendamento ?? false);

  /** O serviço é de agendamento mas não tem agenda: não há horário a oferecer, e não se avança. */
  readonly agendaIndisponivel = computed(
    () => this.exigeAgendamento() && this.formulario()?.bitAgendaDisponivel === false,
  );

  /**
   * Por que o serviço não pode ser pedido NESTE modo de acesso — ou `null` quando pode. O card
   * de acesso já esconde o anônimo quando ele não vale; o login, não, e é aqui que a etapa sabe.
   */
  readonly bloqueioSolicitacao = computed<string | null>(() => {
    const formulario = this.formulario();

    if (!formulario) return null;

    if (!formulario.bitAceitaSolicitacao) return 'Este serviço não recebe solicitação on-line.';

    if (!this.anonimo() && formulario.bitExigirGovBrSrv) {
      return 'Este serviço exige acesso pelo gov.br, que ainda não está disponível no portal.';
    }

    return null;
  });

  readonly dados = signal<DadosSolicitacaoServico>({ ...VALOR_INICIAL });

  readonly solicitacaoForm = form(this.dados, (caminho) => {
    required(caminho.endereco, { message: MENSAGEM_OBRIGATORIO });
    maxLength(caminho.endereco, 300);

    required(caminho.numero, { message: MENSAGEM_OBRIGATORIO });
    maxLength(caminho.numero, 20);

    maxLength(caminho.complemento, 200);

    required(caminho.cep, { message: MENSAGEM_OBRIGATORIO });
    pattern(caminho.cep, /^\d{5}-?\d{3}$/, { message: 'Informe o CEP com 8 dígitos.' });

    required(caminho.bairro, { message: MENSAGEM_OBRIGATORIO });
    maxLength(caminho.bairro, 200);

    required(caminho.cidade, { message: MENSAGEM_OBRIGATORIO });
    maxLength(caminho.cidade, 200);

    required(caminho.uf, { message: MENSAGEM_OBRIGATORIO });
    validate(caminho.uf, ({ value }) =>
      !value() || UFS.includes(value()) ? null : requiredError({ message: 'Escolha uma UF válida.' }),
    );

    required(caminho.descricao, { message: MENSAGEM_OBRIGATORIO });
    maxLength(caminho.descricao, LIMITE_DESCRICAO, {
      message: `A descrição deve ter no máximo ${LIMITE_DESCRICAO} caracteres.`,
    });

    maxLength(caminho.observacoes, LIMITE_OBSERVACOES, {
      message: `As observações devem ter no máximo ${LIMITE_OBSERVACOES} caracteres.`,
    });
  });

  /** Respostas dos campos específicos, por código do campo. */
  readonly valoresCampos = signal<Readonly<Record<number, string>>>({});

  /** Arquivos dos campos 'Upload de Arquivo', por código do campo. */
  readonly arquivosCampos = signal<Readonly<Record<number, File>>>({});

  readonly arquivos = signal<readonly ArquivoAnexoSolicitacao[]>([]);

  readonly agendamento = signal<AgendamentoEscolhido | null>(null);

  readonly declaracaoAceita = signal(false);

  /** "Segunda via" no tipo do pedido. Só vale no serviço que a emite — fora dele, vai `false`. */
  readonly segundaVia = signal(false);

  readonly permiteSegundaVia = computed(() => this.formulario()?.bitPermiteSegundaViaSrv ?? false);

  /** O cidadão tentou avançar com pendência: a etapa passa a destacar os campos. */
  readonly pendenciasVisiveis = signal(false);

  /** O 201 da abertura — com ele, a Confirmação vira recibo. */
  readonly respostaAbertura = signal<SolicitacaoAberturaResponse | null>(null);

  /** O erro da API em tratamento local; a etapa de destino o exibe. */
  readonly erroEnvio = signal<ErroSolicitacaoServico | null>(null);

  /**
   * O desafio de formulário e o honeypot desta solicitação (change
   * anti-abuso-superficie-publica): pedido ao liberar a etapa Solicitação, enviado no cabeçalho do
   * POST, descartado no cancelar.
   */
  readonly desafio = new DesafioDoFormulario(inject(DesafioFormularioService));

  readonly regrasAnexo = computed(() => this.formulario()?.anexos ?? null);

  /** Os formatos de um campo de upload: os do serviço ou, se ele não tem nenhum, o catálogo. */
  readonly formatosCampoUpload = computed(() => {
    const doServico = this.regrasAnexo()?.formatos ?? [];

    return doServico.length > 0 ? doServico : TODOS_OS_FORMATOS;
  });

  readonly tetoBytes = computed(() => this.regrasAnexo()?.numTamanhoMaximoBytes ?? DEZ_MEGABYTES);

  /** Mensagem de pendência de cada campo específico, por código; ausente quando está ok. */
  readonly pendenciasCampos = computed<Readonly<Record<number, string>>>(() => {
    const valores = this.valoresCampos();
    const arquivos = this.arquivosCampos();
    const pendencias: Record<number, string> = {};

    for (const campo of this.campos()) {
      const erro = conferirCampo(campo, valores[campo.codCampoServicoCps] ?? '', arquivos[campo.codCampoServicoCps]);

      if (erro) pendencias[campo.codCampoServicoCps] = erro;
    }

    return pendencias;
  });

  readonly validoSolicitacao = computed(
    () =>
      this.formulario() !== null &&
      this.bloqueioSolicitacao() === null &&
      this.solicitacaoForm().valid() &&
      Object.keys(this.pendenciasCampos()).length === 0 &&
      !this.agendaIndisponivel() &&
      (!this.exigeAgendamento() || this.agendamento() !== null),
  );

  readonly anexoObrigatorioPendente = computed(() => {
    const regras = this.regrasAnexo();

    return !!regras?.bitPermitirAnexosSrv && regras.bitAnexoObrigatorioSrv && this.arquivos().length === 0;
  });

  readonly validoAnexos = computed(() => !this.anexoObrigatorioPendente());

  readonly podeConcluir = computed(
    () => this.declaracaoAceita() && this.validoSolicitacao() && this.validoAnexos(),
  );

  /** Só há o que cancelar depois que algo foi preenchido. */
  readonly preenchido = computed(
    () =>
      Object.values(this.dados()).some((valor) => valor.trim() !== '') ||
      Object.values(this.valoresCampos()).some((valor) => valor.trim() !== '') ||
      Object.keys(this.arquivosCampos()).length > 0 ||
      this.arquivos().length > 0 ||
      this.agendamento() !== null,
  );

  private proximoId = 1;

  destacarPendencias(): void {
    this.pendenciasVisiveis.set(true);
    this.solicitacaoForm().markAsTouched();
  }

  recarregarFormulario(): void {
    this.api.recarregarFormulario();
  }

  entrarAnonimo(): void {
    this.modoAcesso.set('anonimo');
  }

  entrarIdentificado(): void {
    this.modoAcesso.set('identificado');
  }

  definirValorCampo(codCampo: number, valor: string): void {
    this.valoresCampos.update((atuais) => ({ ...atuais, [codCampo]: valor }));
  }

  /** Guarda (ou remove, com `null`) o arquivo de um campo. Devolve a recusa, se houver. */
  definirArquivoCampo(codCampo: number, arquivo: File | null): string | null {
    if (arquivo) {
      const recusa = conferirArquivo(arquivo, this.formatosCampoUpload(), this.tetoBytes());

      if (recusa) return recusa;
    }

    this.arquivosCampos.update((atuais) => {
      const proximos = { ...atuais };

      if (arquivo) proximos[codCampo] = arquivo;
      else delete proximos[codCampo];

      return proximos;
    });

    return null;
  }

  /**
   * Adiciona os arquivos que passam nas regras do serviço e devolve as recusas — cada uma com
   * o motivo, para a etapa mostrar sem perder os que entraram.
   */
  adicionarArquivos(lista: FileList | readonly File[] | null | undefined): readonly string[] {
    const regras = this.regrasAnexo();

    if (!lista?.length || !regras) return [];

    if (!regras.bitPermitirAnexosSrv) return ['Este serviço não recebe anexos.'];

    const recusas: string[] = [];
    const aceitos: ArquivoAnexoSolicitacao[] = [];

    for (const arquivo of Array.from(lista)) {
      if (this.arquivos().length + aceitos.length >= regras.numQuantidadeMaximaSrv) {
        recusas.push(`Este serviço aceita no máximo ${regras.numQuantidadeMaximaSrv} anexo(s).`);
        break;
      }

      const recusa = conferirArquivo(arquivo, regras.formatos, regras.numTamanhoMaximoBytes);

      if (recusa) {
        recusas.push(recusa);
        continue;
      }

      aceitos.push({ id: this.proximoId++, nome: arquivo.name, tamanho: arquivo.size, arquivo });
    }

    this.arquivos.update((atuais) => [...atuais, ...aceitos]);

    return recusas;
  }

  removerArquivo(id: number): void {
    this.arquivos.update((lista) => lista.filter((arquivo) => arquivo.id !== id));
  }

  /** O multipart da abertura, só com o que o back-end aceita. */
  paraFormData(codServicoSrv: number): FormData {
    const dados = this.dados();

    const corpo: DadosAberturaSolicitacao = {
      codServicoSrv,
      anonima: this.anonimo(),
      segundaVia: this.permiteSegundaVia() && this.segundaVia(),
      endereco: dados.endereco,
      numero: dados.numero,
      complemento: dados.complemento,
      cep: dados.cep,
      bairro: dados.bairro,
      cidade: dados.cidade,
      uf: dados.uf,
      descricao: dados.descricao,
      observacoes: dados.observacoes,
      agendamento: this.exigeAgendamento() ? this.agendamento() : null,
      campos: this.campos().map((campo) => ({
        codCampoServicoCps: campo.codCampoServicoCps,
        valor: campo.desTipoCps === 'Upload de Arquivo' ? '' : (this.valoresCampos()[campo.codCampoServicoCps] ?? ''),
        arquivo: campo.desTipoCps === 'Upload de Arquivo' ? (this.arquivosCampos()[campo.codCampoServicoCps] ?? null) : null,
      })),
      anexos: this.arquivos().map((anexo) => anexo.arquivo),
      honeypot: this.desafio.honeypot(),
    };

    return montarFormDataSolicitacao(corpo);
  }

  /** Depois de um 409 de formulário desatualizado: as respostas antigas apontam para campos que não existem mais. */
  descartarRespostasCampos(): void {
    this.valoresCampos.set({});
    this.arquivosCampos.set({});
  }

  limpar(): void {
    this.modoAcesso.set('');
    this.dados.set({ ...VALOR_INICIAL });
    this.solicitacaoForm().reset();
    this.valoresCampos.set({});
    this.arquivosCampos.set({});
    this.arquivos.set([]);
    this.agendamento.set(null);
    this.declaracaoAceita.set(false);
    this.segundaVia.set(false);
    this.pendenciasVisiveis.set(false);
    this.respostaAbertura.set(null);
    this.erroEnvio.set(null);

    // A próxima liberação da etapa Solicitação pede outro desafio.
    this.desafio.descartar();
  }
}

/** A regra de UM campo específico — a mesma do back-end (`SolicitacaoCamposHelper`). */
export function conferirCampo(
  campo: CampoFormularioPublicoResponse,
  valorBruto: string,
  arquivo: File | undefined,
): string | null {
  if (campo.desTipoCps === 'Upload de Arquivo') {
    return !arquivo && campo.bitObrigatorioCps ? `Anexe o arquivo de "${campo.desNomeCps}".` : null;
  }

  const valor = valorBruto.trim();

  if (!valor) return campo.bitObrigatorioCps ? MENSAGEM_OBRIGATORIO : null;

  switch (campo.desTipoCps) {
    case 'Texto Curto':
      return valor.length > LIMITE_TEXTO_CURTO ? `Use no máximo ${LIMITE_TEXTO_CURTO} caracteres.` : null;
    case 'Texto Longo':
      return valor.length > LIMITE_TEXTO_LONGO ? `Use no máximo ${LIMITE_TEXTO_LONGO} caracteres.` : null;
    case 'Número':
      return /^-?\d+(\.\d+)?$/.test(valor) ? null : 'Informe um número (use ponto para decimais).';
    case 'Data':
      return /^\d{4}-\d{2}-\d{2}$/.test(valor) ? null : 'Informe uma data válida.';
    case 'Seleção':
      return campo.opcoes.includes(valor) ? null : 'Escolha uma das opções.';
    default:
      return null;
  }
}
